import { useState } from "react";
import styles from "./SubscribersManager.module.css";
import ToggleSwitch from "../../../components/BaseComponents/Toggle/ToggleSwitch";
import ViewSubscriberModal from "../../../components/Subscribers/ViewSubscriberModal";
import UnsubscribeModal from "../../../components/Subscribers/UnsubscribeModal";
import { useToast } from "../../../context/ToastContext";
import {
  useSubscribersList,
  useUpdateSubscriber,
  useUnsubscribeSubscriber,
} from "../../../api/cms/useSubscribersApi";

const SubscribersManager = () => {
  const { showToast } = useToast();
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [showView, setShowView] = useState(false);
  const [showUnsub, setShowUnsub] = useState(false);

  const { data, loading, error, refetch } = useSubscribersList({
    limit: 100,
    page: 1,
    search: search.trim() || undefined,
  });
  const rows = Array.isArray(data) ? data : [];

  const { execute: updateSubscriber } = useUpdateSubscriber();
  const { execute: unsubscribeSubscriber } = useUnsubscribeSubscriber();

  const handleToggleStatus = async (id, currentActive) => {
    try {
      await updateSubscriber(id, { active: !currentActive });
      refetch();
      showToast("Subscriber updated successfully", "success");
    } catch (err) {
      showToast(err.response?.data?.message || "Failed to update subscriber", "error");
    }
  };

  const handleUnsubscribeConfirm = async () => {
    if (!selected?.id) return;
    try {
      await unsubscribeSubscriber(selected.id, { event: "Unsubscribed manually" });
      setShowUnsub(false);
      setSelected(null);
      refetch();
      showToast("Subscriber unsubscribed successfully", "success");
    } catch (err) {
      showToast(err.response?.data?.message || "Failed to unsubscribe", "error");
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.toolbar}>
        <div className={styles.manager}>
          Subscribers
          <span className={styles.subtitle}>
            CMS Newsletter & marketing subscribers
          </span>
        </div>

        <input
          className={styles.search}
          placeholder="Search by email"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {error && <div className={styles.error}>{error}</div>}
      {loading && <div className={styles.loading}>Loading subscribers…</div>}

      {!loading && (
        <div className={styles.tableSection}>
          <div className={styles.tableCard}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Name</th>
                  <th>Source</th>
                  <th>Subscribed</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>

              <tbody>
                {rows?.length === 0 ? (
                  <tr>
                    <td colSpan={6} className={styles.emptyCell}>
                      No subscribers found.
                    </td>
                  </tr>
                ) : (
                  rows.map((row) => (
                <tr key={row.id}>
                  <td className={styles.email}>{row.email}</td>

                  <td>
                    {row.firstName || row.lastName
                      ? `${row.firstName || ""} ${row.lastName || ""}`.trim()
                      : "—"}
                  </td>

                  <td>{row.source || "—"}</td>
                  <td>{row.subscribedAt || "—"}</td>

                  <td>
                    <ToggleSwitch
                      checked={!!row.active}
                      onChange={() => handleToggleStatus(row.id, row.active)}
                    />
                  </td>

                  <td className={styles.actions}>
                    <div className={styles.actionGroup}>
                      <button
                        className={styles.viewBtn}
                        onClick={() => {
                          setSelected(row);
                          setShowView(true);
                        }}
                      >
                        View
                      </button>

                      <button
                        className={styles.unsubscribeBtn}
                        onClick={() => {
                          setSelected(row);
                          setShowUnsub(true);
                        }}
                        disabled={!row.active}
                      >
                        Unsubscribe
                      </button>
                    </div>
                  </td>
                </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className={styles.footerBar}>
            <span className={styles.footerCount}>
              {rows.length} subscriber{rows.length !== 1 ? "s" : ""}
            </span>
          </div>
        </div>
      )}

      <ViewSubscriberModal
        open={showView}
        onClose={() => { setShowView(false); setSelected(null); }}
        data={selected}
      />

      <UnsubscribeModal
        open={showUnsub}
        onClose={() => { setShowUnsub(false); setSelected(null); }}
        data={selected}
        onConfirm={handleUnsubscribeConfirm}
      />
    </div>
  );
};

export default SubscribersManager;
